import React, { useState, useEffect } from 'react';
import { Compass, Lightbulb, TrendingUp, Package, Wrench, Lock, Unlock, Info, Loader2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { productsApi, organizationsApi } from '../../services/api';

// Module definitions
const NAVIGATION_MODULES = [
  {
    id: 'ideation',
    name: 'Ideation',
    description: 'Ideation and Requests',
    icon: Lightbulb,
    tabs: ['Ideation', 'Requests'],
    color: 'text-amber-500'
  },
  {
    id: 'market',
    name: 'Market',
    description: 'Market Research',
    icon: TrendingUp,
    tabs: ['Market'],
    color: 'text-green-500'
  },
  {
    id: 'planning',
    name: 'Planning & Releases',
    description: 'Releases & Kanban',
    icon: Package,
    tabs: ['Releases', 'Kanban'],
    color: 'text-blue-500'
  },
  {
    id: 'build',
    name: 'Build',
    description: 'Architecture and Delivery',
    icon: Wrench,
    tabs: ['Architecture', 'Delivery'],
    color: 'text-purple-500'
  }
];

// Organization-level Navigation Settings
export const OrgNavigationSettings = ({ organization, onSave, canEdit = true }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [settings, setSettings] = useState({
    ideation_enabled: true,
    market_enabled: true,
    planning_enabled: true,
    build_enabled: true,
    ideation_locked: false,
    market_locked: false,
    planning_locked: false,
    build_locked: false,
    allow_product_override: true
  });
  
  const [saving, setSaving] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [applyingToProducts, setApplyingToProducts] = useState(false);
  
  // Load existing settings
  useEffect(() => {
    if (organization?.navigation_defaults) {
      setSettings(prev => ({ ...prev, ...organization.navigation_defaults }));
    }
  }, [organization]);
  
  const handleToggle = (field) => {
    setSettings(prev => ({ ...prev, [field]: !prev[field] }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ navigation_defaults: settings });
      toast.success('Navigation defaults saved');
    } catch (err) {
      toast.error('Failed to save navigation settings');
    } finally {
      setSaving(false);
    }
  };
  
  const openApplyModal = async () => {
    setShowApplyModal(true);
    setLoadingProducts(true);
    try {
      const res = await productsApi.list();
      const activeProducts = (res.data || []).filter(p => !p.is_archived);
      setProducts(activeProducts);
      setSelectedProducts(activeProducts.map(p => p.product_id)); // Select all by default
    } catch (err) {
      console.error('Failed to load products:', err);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };
  
  const handleApplyToProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    setApplyingToProducts(true);
    try {
      const navSettings = {
        ideation_enabled: settings.ideation_enabled,
        market_enabled: settings.market_enabled,
        planning_enabled: settings.planning_enabled,
        build_enabled: settings.build_enabled
      };
      
      await organizationsApi.applyNavigationToProducts(selectedProducts, navSettings);
      toast.success(`Applied settings to ${selectedProducts.length} product(s)`);
      setShowApplyModal(false);
    } catch (err) {
      console.error('Failed to apply settings:', err);
      toast.error('Failed to apply settings to products');
    } finally {
      setApplyingToProducts(false);
    }
  };
  
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };
  
  const toggleAllProducts = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.product_id));
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Description */}
      <p className={cn("text-sm", colors.textMuted)}>
        Configure which modules are visible by default across all products.
      </p>
      
      {/* Product Core Notice */}
      <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
        <div className="flex items-start gap-3">
          <Compass className={cn("w-5 h-5 mt-0.5", colors.textAccent)} />
          <div>
            <p className={cn("font-medium", colors.textPrimary)}>Product Core (Always Visible)</p>
            <p className={cn("text-sm mt-1", colors.textMuted)}>
              Strategy, Personas, Goals, Initiatives, Epics, Features
            </p>
          </div>
        </div>
      </div>
      
      {/* Module Toggles */}
      <div className="space-y-3">
        {NAVIGATION_MODULES.map(module => {
          const enabledKey = `${module.id}_enabled`;
          const lockedKey = `${module.id}_locked`;
          const ModuleIcon = module.icon;
          
          return (
            <div 
              key={module.id}
              className={cn(
                "p-4 rounded-lg border transition-colors",
                settings[enabledKey] ? colors.bgSecondary : cn(colors.bgTertiary, "opacity-60"),
                colors.borderPrimary
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ModuleIcon className={cn("w-5 h-5", module.color)} />
                  <div>
                    <p className={cn("font-medium", colors.textPrimary)}>{module.name}</p>
                    <p className={cn("text-sm", colors.textMuted)}>{module.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Lock toggle */}
                  <button
                    onClick={() => canEdit && handleToggle(lockedKey)}
                    disabled={!canEdit}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      settings[lockedKey] 
                        ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600" 
                        : cn(colors.bgHover, colors.textMuted)
                    )}
                    title={settings[lockedKey] ? "Locked - Products cannot override" : "Unlocked - Products can override"}
                  >
                    {settings[lockedKey] ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                  </button>
                  
                  {/* Enable toggle */}
                  <Switch
                    checked={settings[enabledKey]}
                    onCheckedChange={() => canEdit && handleToggle(enabledKey)}
                    disabled={!canEdit}
                  />
                </div>
              </div>
              
              {/* Hidden tabs hint */}
              <div className="mt-2 flex flex-wrap gap-1">
                {module.tabs.map(tab => (
                  <span 
                    key={tab}
                    className={cn(
                      "text-xs px-2 py-0.5 rounded",
                      settings[enabledKey] 
                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-500 line-through"
                    )}
                  >
                    {tab}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Allow Override Toggle */}
      <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
        <div className="flex items-center justify-between">
          <div>
            <p className={cn("font-medium", colors.textPrimary)}>Allow Product Customization</p>
            <p className={cn("text-sm", colors.textMuted)}>
              Let product owners/managers customize navigation for individual products
            </p>
          </div>
          <Switch
            checked={settings.allow_product_override}
            onCheckedChange={() => canEdit && handleToggle('allow_product_override')}
            disabled={!canEdit}
          />
        </div>
      </div>
      
      {/* Info Notice */}
      <div className={cn("p-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20")}>
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-500 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Hiding modules does not delete any data. All information is preserved and will reappear when the module is re-enabled.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      {canEdit && (
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Defaults
          </Button>
          <Button
            variant="outline"
            onClick={openApplyModal}
            className={cn("border", colors.borderPrimary)}
          >
            Apply to Existing Products...
          </Button>
        </div>
      )}
      
      {/* Apply to Products Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className={cn("max-w-md", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className={colors.textPrimary}>Apply Navigation Settings</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className={cn("text-sm mb-4", colors.textMuted)}>
              Select which products should receive the current navigation settings:
            </p>
            
            {loadingProducts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
              </div>
            ) : products.length === 0 ? (
              <p className={cn("text-center py-8", colors.textMuted)}>No products found</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {/* Select All */}
                <label className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border cursor-pointer",
                  colors.bgSecondary, colors.borderPrimary
                )}>
                  <Checkbox
                    checked={selectedProducts.length === products.length}
                    onCheckedChange={toggleAllProducts}
                  />
                  <span className={cn("font-medium", colors.textPrimary)}>
                    Select All ({products.length} products)
                  </span>
                </label>
                
                <div className="border-t my-2" />
                
                {/* Individual products */}
                {products.map(product => (
                  <label 
                    key={product.product_id}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                      selectedProducts.includes(product.product_id) 
                        ? "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700"
                        : cn(colors.bgSecondary, colors.borderPrimary)
                    )}
                  >
                    <Checkbox
                      checked={selectedProducts.includes(product.product_id)}
                      onCheckedChange={() => toggleProductSelection(product.product_id)}
                    />
                    <span className={colors.textPrimary}>{product.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyToProducts}
              disabled={applyingToProducts || selectedProducts.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {applyingToProducts ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Apply to {selectedProducts.length} Product{selectedProducts.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Product-level Navigation Settings (for ProductSettingsModal)
export const ProductNavigationSettings = ({ 
  product, 
  organization,
  canEdit = true,
  onChange 
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const orgDefaults = organization?.navigation_defaults || {};
  const allowOverride = orgDefaults.allow_product_override !== false;
  
  // Derive initial settings from product or org defaults
  const getInitialSettings = () => {
    const navSettings = product?.navigation_settings;
    if (navSettings) {
      return {
        ideation_enabled: navSettings.ideation_enabled !== false,
        market_enabled: navSettings.market_enabled !== false,
        planning_enabled: navSettings.planning_enabled !== false,
        build_enabled: navSettings.build_enabled !== false
      };
    }
    return {
      ideation_enabled: orgDefaults.ideation_enabled !== false,
      market_enabled: orgDefaults.market_enabled !== false,
      planning_enabled: orgDefaults.planning_enabled !== false,
      build_enabled: orgDefaults.build_enabled !== false
    };
  };
  
  const [settings, setSettings] = useState(getInitialSettings);
  
  const handleToggle = (moduleId) => {
    const key = `${moduleId}_enabled`;
    const lockedKey = `${moduleId}_locked`;
    
    // Check if locked at org level
    if (orgDefaults[lockedKey]) {
      toast.error('This module is locked by your organization');
      return;
    }
    
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    onChange?.(newSettings);
  };
  
  const isModuleLocked = (moduleId) => {
    return orgDefaults[`${moduleId}_locked`] === true;
  };
  
  if (!allowOverride && !canEdit) {
    return (
      <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
        <p className={cn("text-sm", colors.textMuted)}>
          Navigation customization is disabled for this organization. 
          Contact your organization owner to enable it.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Navigation</h3>
        <p className={cn("text-sm", colors.textMuted)}>
          Choose which modules are visible for this product.
        </p>
      </div>
      
      {/* Product Core Notice */}
      <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
        <div className="flex items-start gap-3">
          <Compass className={cn("w-5 h-5 mt-0.5", colors.textAccent)} />
          <div>
            <p className={cn("font-medium", colors.textPrimary)}>Product Core (Always Visible)</p>
            <p className={cn("text-sm mt-1", colors.textMuted)}>
              Strategy, Personas, Goals, Initiatives, Epics, Features
            </p>
          </div>
        </div>
      </div>
      
      {/* Module Toggles */}
      <div className="space-y-3">
        {NAVIGATION_MODULES.map(module => {
          const enabledKey = `${module.id}_enabled`;
          const isLocked = isModuleLocked(module.id);
          const ModuleIcon = module.icon;
          
          return (
            <div 
              key={module.id}
              className={cn(
                "p-4 rounded-lg border transition-colors",
                settings[enabledKey] ? colors.bgSecondary : cn(colors.bgTertiary, "opacity-60"),
                isLocked && "border-amber-300 dark:border-amber-700",
                colors.borderPrimary
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ModuleIcon className={cn("w-5 h-5", module.color)} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={cn("font-medium", colors.textPrimary)}>{module.name}</p>
                      {isLocked && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                          <Lock className="w-3 h-3" />
                          Locked
                        </span>
                      )}
                    </div>
                    <p className={cn("text-sm", colors.textMuted)}>{module.description}</p>
                  </div>
                </div>
                
                <Switch
                  checked={settings[enabledKey]}
                  onCheckedChange={() => handleToggle(module.id)}
                  disabled={!canEdit || isLocked}
                />
              </div>
              
              {/* Hidden tabs hint */}
              <div className="mt-2 flex flex-wrap gap-1">
                {module.tabs.map(tab => (
                  <span 
                    key={tab}
                    className={cn(
                      "text-xs px-2 py-0.5 rounded",
                      settings[enabledKey] 
                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-500 line-through"
                    )}
                  >
                    {tab}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Info Notice */}
      <div className={cn("p-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20")}>
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-500 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Hiding modules does not delete any data. All information is preserved and will reappear when the module is re-enabled.
          </p>
        </div>
      </div>
    </div>
  );
};

export default { OrgNavigationSettings, ProductNavigationSettings };
